import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { saveTariffa, deleteTariffa, db } from '../services/firebase';

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelBody, Button, Spinner } from '../components/ui';
import { useWindowSize } from '../hooks/useWindowSize';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getRigheServizio(servizioId, layoutTemplate, gridRows) {
  const righe = [...new Set(
    layoutTemplate
      .filter(c => c.type === servizioId)
      .map(c => c.row)
  )].sort((a, b) => a - b);
  return righe.length > 0 ? righe : Array.from({ length: gridRows }, (_, i) => i);
}

// ─── Import CSV helpers ───────────────────────────────────────────────────────

const MACRAUME_EXTRA = 5;

function estraiPrezzoGiornaliero(headers, valoriDe) {
  const get = (col) => {
    const idx = headers.indexOf(col);
    return idx !== -1 ? parseFloat(valoriDe[idx]) || 0 : 0;
  };
  return {
    s1: get('S1+1U') || get('S1+1C') || 0,
    s2: get('S2+1U') || get('S2+1C') || 0,
    s3: get('S3+1U') || get('S3+1C') || 0,
    s4: get('S4+1U') || get('S4+1C') || get('S4+1M') || 0,
    s6: get('S6+1U') || get('S6+1B') || 0,
  };
}

function estraiDateDaNomeFile(nomeFile) {
  const anno = 2026;

  // "periodo-DD_MM-DD_MM"
  const m1 = nomeFile.match(/periodo-(\d{1,2})_(\d{2})-(\d{1,2})_(\d{2})/);
  if (m1) {
    const [, gg1, mm1, gg2, mm2] = m1;
    return {
      dal: `${anno}-${mm1.padStart(2, '0')}-${gg1.padStart(2, '0')}`,
      al: `${anno}-${mm2.padStart(2, '0')}-${gg2.padStart(2, '0')}`,
    };
  }

  // "periodo-D-DD_MM" (es. periodo-1-29_05)
  const m2 = nomeFile.match(/periodo-(\d{1,2})-(\d{1,2})_(\d{2})(?!\d)/);
  if (m2) {
    const [, gg1, gg2, mm] = m2;
    return {
      dal: `${anno}-${mm.padStart(2, '0')}-${gg1.padStart(2, '0')}`,
      al: `${anno}-${mm.padStart(2, '0')}-${gg2.padStart(2, '0')}`,
    };
  }

  // "periodo-DD-DD_MM" (es. periodo-14-16_08)
  const m3 = nomeFile.match(/periodo-(\d{2})-(\d{2})_(\d{2})/);
  if (m3) {
    const [, gg1, gg2, mm] = m3;
    return {
      dal: `${anno}-${mm.padStart(2, '0')}-${gg1.padStart(2, '0')}`,
      al: `${anno}-${mm.padStart(2, '0')}-${gg2.padStart(2, '0')}`,
    };
  }

  return null;
}

function calcolaPriorita(dal, al) {
  const giorni = (new Date(al) - new Date(dal)) / 86400000 + 1;
  return Math.max(1, Math.round(100 - giorni));
}

function parseCSV(testo) {
  const righe = testo.split('\n').map(r => r.trim()).filter(Boolean)
    .filter(r => !r.startsWith('"sep='));
  if (righe.length < 2) return null;
  const headers = righe[0].split(',');
  const rigaDe = righe.find(r => r.startsWith('de,'));
  if (!rigaDe) return null;
  return { headers, valoriDe: rigaDe.split(',') };
}

function generaNome(dal, al) {
  const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  const d1 = new Date(dal), d2 = new Date(al);
  const m1 = mesi[d1.getMonth()], m2 = mesi[d2.getMonth()];
  if (m1 === m2) return `${d1.getDate()}–${d2.getDate()} ${m1}`;
  return `${d1.getDate()} ${m1} – ${d2.getDate()} ${m2}`;
}

// ─── Pagina principale ────────────────────────────────────────────────────────

export default function Tariffe() {
  const { beach, admin, tariffe: tariffeCached, refreshBeach } = useAuth();
  const { isMobile } = useWindowSize();
  const [tariffe, setTariffe] = useState(tariffeCached || []);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const servizi = beach?.servizi || [];
  const gridRows = beach?.gridRows || 5;
  const layoutTemplate = beach?.layoutTemplate || [];

  useEffect(() => { setTariffe(tariffeCached); }, [tariffeCached]);

  const handleSave = async (tariffa) => {
    setLoading(true);
    try {
      await saveTariffa(admin.beachCode, tariffa);
      await refreshBeach();
      setEditing(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa fascia tariffaria?')) return;
    await deleteTariffa(admin.beachCode, id);
    await refreshBeach();
  };

  const getSovrapposizioni = (t) =>
    tariffe.filter(other =>
      other.id !== t.id && other.dal <= t.al && other.al >= t.dal
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar
        title="Tariffe stagionali"
        subtitle="Gestisci i prezzi per periodo"
        actions={
          <Button variant="primary" onClick={() => setEditing({})}>
            + Nuova fascia
          </Button>
        }
      />

      <div style={{ padding: isMobile ? '12px 16px' : '24px 28px', overflowY: 'auto', flex: 1 }}>

        {/* ── Import CSV ── */}
        <ImportCSVTariffe
          beachCode={admin.beachCode}
          servizi={servizi}
          layoutTemplate={layoutTemplate}
          gridRows={gridRows}
          onImportComplete={() => refreshBeach()}
        />

        {/* ── Form nuova/modifica ── */}
        {editing !== null && (
          <TariffaForm
            tariffa={editing}
            servizi={servizi}
            gridRows={gridRows}
            layoutTemplate={layoutTemplate}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
            loading={loading}
          />
        )}

        {/* ── Lista tariffe ── */}
        {tariffe.length === 0 && editing === null ? (
          <Panel>
            <PanelBody>
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>💰</div>
                <div style={{ fontSize: '13px', marginBottom: '16px' }}>
                  Nessuna fascia tariffaria configurata.<br />
                  Crea la tua prima tariffa stagionale o importa un CSV da Spiagge.it.
                </div>
                <Button variant="primary" onClick={() => setEditing({})}>
                  + Crea prima tariffa
                </Button>
              </div>
            </PanelBody>
          </Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {tariffe.map((t) => (
              <TariffaCard
                key={t.id}
                tariffa={t}
                servizi={servizi}
                gridRows={gridRows}
                layoutTemplate={layoutTemplate}
                sovrapposizioni={getSovrapposizioni(t).length}
                onEdit={() => setEditing(t)}
                onDelete={() => handleDelete(t.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TariffaCard ─────────────────────────────────────────────────────────────

function TariffaCard({ tariffa, servizi, gridRows, layoutTemplate, sovrapposizioni, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const { isMobile } = useWindowSize();
  const today = new Date().toISOString().split('T')[0];
  const isActive = tariffa.dal <= today && tariffa.al >= today;
  const isFutura = tariffa.dal > today;

  return (
    <Panel>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: isMobile ? '12px 16px' : '16px 20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}
      >
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
          background: isActive ? '#4ade80' : isFutura ? 'var(--sand)' : 'var(--text-muted)',
        }} />

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>
            {tariffa.nome}
            {tariffa.fonte === 'import_csv_spiagge_it' && (
              <span style={{
                marginLeft: 8,
                fontSize: 10,
                fontWeight: 600,
                padding: '1px 7px',
                borderRadius: 10,
                background: 'rgba(31,147,224,0.12)',
                color: 'var(--ocean)',
                border: '1px solid rgba(31,147,224,0.25)',
                verticalAlign: 'middle',
              }}>
                CSV
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {tariffa.dal} → {tariffa.al}
            {isActive && <span style={{ color: '#4ade80', marginLeft: '8px' }}>● Attiva</span>}
            {isFutura && <span style={{ color: 'var(--sand)', marginLeft: '8px' }}>● Futura</span>}
          </div>
        </div>

        {sovrapposizioni > 0 && (
          <div style={{
            padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
            background: 'rgba(245,200,66,0.15)', color: 'var(--sand)',
            border: '1px solid rgba(245,200,66,0.3)',
          }}>
            ⚠ {sovrapposizioni} sovrapposiz.
          </div>
        )}

        <div style={{ display: 'flex', gap: '6px', marginLeft: isMobile ? 'auto' : 0 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); onEdit(); }}>
            Modifica
          </Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); onDelete(); }}>
            Elimina
          </Button>
        </div>

        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
          style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: 'var(--text-muted)', flexShrink: 0 }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {expanded && (
        <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--border)' }}>
          <PrezziTable
            tariffa={tariffa}
            servizi={servizi}
            gridRows={gridRows}
            layoutTemplate={layoutTemplate}
          />
        </div>
      )}
    </Panel>
  );
}

// ─── PrezziTable ─────────────────────────────────────────────────────────────

function PrezziTable({ tariffa, servizi, gridRows, layoutTemplate }) {
  return (
    <div style={{ marginTop: '14px', overflowX: 'auto' }}>
      <table style={{ width: '100%', fontSize: '12.5px', borderCollapse: 'collapse' }}>
        <tbody>
          {servizi.map(s => {
            const righe = getRigheServizio(s.id, layoutTemplate, gridRows);
            return (
              <tr key={s.id}>
                <td style={{ padding: '8px 12px 8px 0', color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>
                  <span style={{
                    display: 'inline-block', width: '10px', height: '10px',
                    borderRadius: '50%', background: s.colore || '#1f93e0',
                    marginRight: '8px', verticalAlign: 'middle',
                  }} />
                  {s.label}
                </td>
                {s.haFila ? (
                  righe.map((rowIndex) => {
                    const key = `${s.id}_r${rowIndex}`;
                    const val = tariffa.prezzi?.[key];
                    return (
                      <td key={rowIndex} style={{ padding: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>
                          Fila {LETTERS[rowIndex]}
                        </div>
                        <div style={{ color: val ? 'var(--sand)' : 'var(--text-muted)' }}>
                          {val ? `€ ${val}` : '—'}
                        </div>
                      </td>
                    );
                  })
                ) : (
                  <td style={{ padding: '8px', color: tariffa.prezzi?.[s.id] ? 'var(--sand)' : 'var(--text-muted)' }}>
                    {tariffa.prezzi?.[s.id] ? `€ ${tariffa.prezzi[s.id]}` : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── TariffaForm ─────────────────────────────────────────────────────────────

function TariffaForm({ tariffa, servizi, gridRows, layoutTemplate, onSave, onCancel, loading }) {
  const { isMobile } = useWindowSize();
  const [form, setForm] = useState({
    nome: tariffa.nome || '',
    dal: tariffa.dal || '',
    al: tariffa.al || '',
    prezzi: tariffa.prezzi || {},
    ...(tariffa.id ? { id: tariffa.id } : {}),
  });

  const setPrezzo = (key, val) =>
    setForm(p => ({ ...p, prezzi: { ...p.prezzi, [key]: Number(val) || 0 } }));

  const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%',
  };

  const priceInput = (key) => (
    <div style={{ position: 'relative' }}>
      <span style={{
        position: 'absolute', left: '9px', top: '50%',
        transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--sand)',
      }}>€</span>
      <input
        type="number" min="0" step="0.5"
        value={form.prezzi[key] || ''}
        onChange={e => setPrezzo(key, e.target.value)}
        style={{ ...inputStyle, paddingLeft: '22px', width: '90px', textAlign: 'right' }}
      />
    </div>
  );

  return (
    <div style={{
      background: 'var(--navy-mid)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px',
    }}>
      <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '20px' }}>
        {tariffa.id ? 'Modifica fascia tariffaria' : 'Nuova fascia tariffaria'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '12px', marginBottom: '24px' }}>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Nome fascia</label>
          <input
            placeholder="es. Alta stagione, Ferragosto..."
            value={form.nome}
            onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Dal</label>
          <input type="date" value={form.dal}
            onChange={e => setForm(p => ({ ...p, dal: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Al</label>
          <input type="date" value={form.al}
            onChange={e => setForm(p => ({ ...p, al: e.target.value }))} style={inputStyle} />
        </div>
      </div>

      <div style={{
        background: 'rgba(31,147,224,0.08)', border: '1px solid rgba(31,147,224,0.2)',
        borderRadius: 'var(--radius-md)', padding: '10px 14px',
        fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px',
      }}>
        💡 In caso di date sovrapposte, vince automaticamente la fascia con il{' '}
        <strong style={{ color: 'var(--text-soft)' }}>range più corto</strong> (più specifica).
      </div>

      <div style={{
        fontSize: '12px', color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px',
      }}>
        Prezzi al giorno
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
        {servizi.map(s => {
          const righe = getRigheServizio(s.id, layoutTemplate, gridRows);
          return (
            <div key={s.id} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: s.haFila ? '12px' : '8px' }}>
                <span style={{
                  width: '12px', height: '12px', borderRadius: '50%',
                  background: s.colore || '#1f93e0', flexShrink: 0, display: 'inline-block',
                }} />
                <span style={{ fontSize: '13.5px', fontWeight: 500 }}>{s.label}</span>
                {s.haFila && (
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    — prezzo per fila (riga {LETTERS[righe[0]]} = fronte mare)
                  </span>
                )}
              </div>

              {s.haFila ? (
                <div style={{
                  display: 'flex', gap: '10px',
                  flexWrap: isMobile ? 'nowrap' : 'wrap',
                  overflowX: isMobile ? 'auto' : 'unset',
                  paddingBottom: isMobile ? '4px' : 0,
                  WebkitOverflowScrolling: 'touch',
                }}>
                  {righe.map((rowIndex) => (
                    <div key={rowIndex}>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                        Fila {LETTERS[rowIndex]}{rowIndex === righe[0] ? ' (fronte mare)' : ''}
                      </label>
                      {priceInput(`${s.id}_r${rowIndex}`)}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Prezzo unico:</label>
                  {priceInput(s.id)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {servizi.length === 0 && (
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '16px', textAlign: 'center', marginBottom: '20px' }}>
          Configura prima i servizi nella pagina Servizi
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Button
          variant="primary"
          onClick={() => onSave(form)}
          disabled={loading || !form.nome || !form.dal || !form.al}
        >
          {loading ? <Spinner size={14} /> : tariffa.id ? 'Salva modifiche' : 'Crea fascia'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>Annulla</Button>
      </div>
    </div>
  );
}

// ─── ImportCSVTariffe ─────────────────────────────────────────────────────────

function ImportCSVTariffe({ beachCode, servizi = [], layoutTemplate = [], gridRows = 5, onImportComplete }) {
  const [aperto, setAperto] = useState(false);
  const [anteprima, setAnteprima] = useState([]);
  const [importando, setImportando] = useState(false);
  const [risultato, setRisultato] = useState(null);
  const [erroriParsing, setErroriParsing] = useState([]);
  const inputRef = useRef();

  const ID_OMBRELLONE = 's1776059259879';
  const ID_GAZEBO = 's1778878412074';
  const ID_MACRAUME = 's1778878662013';
  const ID_AREA_LETTINI = 's1776058850700';

  const righeOmbrellone = getRigheServizio(ID_OMBRELLONE, layoutTemplate, gridRows);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setAnteprima([]);
    setErroriParsing([]);
    setRisultato(null);

    const tariffe = [];
    const errori = [];
    const visti = new Set();

    for (const file of files) {
      const testo = await file.text();
      const parsed = parseCSV(testo);

      if (!parsed) {
        errori.push(`${file.name}: impossibile parsare il CSV`);
        continue;
      }

      const date = estraiDateDaNomeFile(file.name);
      if (!date) {
        errori.push(`${file.name}: impossibile estrarre le date dal nome file`);
        continue;
      }

      const chiave = `${date.dal}_${date.al}`;
      if (visti.has(chiave)) continue;
      visti.add(chiave);

      const { headers, valoriDe } = parsed;
      const tuttiVuoti = valoriDe.slice(1).every(v => v.trim() === '');
      if (tuttiVuoti) {
        errori.push(`${file.name}: file senza dati (saltato)`);
        continue;
      }

      const prezzi = estraiPrezzoGiornaliero(headers, valoriDe);

      // Mappa S1/S2/S3 → chiavi _r{rowIndex} reali del layout
      const prezziOmbrellone = {};
      [prezzi.s1, prezzi.s2, prezzi.s3].forEach((prezzo, i) => {
        const rowIndex = righeOmbrellone[i];
        if (rowIndex !== undefined) {
          prezziOmbrellone[`${ID_OMBRELLONE}_r${rowIndex}`] = prezzo;
        }
      });

      tariffe.push({
        nome: generaNome(date.dal, date.al),
        dal: date.dal,
        al: date.al,
        priorita: calcolaPriorita(date.dal, date.al),
        prezzi: {
          ...prezziOmbrellone,
          [ID_GAZEBO]: prezzi.s4,
          [ID_MACRAUME]: prezzi.s1 > 0 ? prezzi.s1 + MACRAUME_EXTRA : 0,
          [ID_AREA_LETTINI]: prezzi.s6,
        },
        _nomeFile: file.name,
      });
    }

    tariffe.sort((a, b) => a.dal.localeCompare(b.dal));
    setAnteprima(tariffe);
    setErroriParsing(errori);
  };

  const handleImport = async () => {
    if (!anteprima.length) return;
    setImportando(true);

    const ok = [], errori = [];
    for (const t of anteprima) {
      try {
        const { _nomeFile, ...dati } = t;
        await addDoc(collection(db, 'lidi', beachCode, 'tariffe'), {
          ...dati,
          createdAt: serverTimestamp(),
          fonte: 'import_csv_spiagge_it',
        });
        ok.push(t.nome);
      } catch (err) {
        errori.push(`${t.nome}: ${err.message}`);
      }
    }

    setImportando(false);
    setRisultato({ ok, errori });
    if (ok.length > 0 && onImportComplete) onImportComplete();
  };

  const reset = () => {
    setAnteprima([]);
    setErroriParsing([]);
    setRisultato(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  // ── Stili ────────────────────────────────────────────────────────────────

  const btn = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '9px 16px',
    background: 'var(--ocean)', color: '#fff',
    border: 'none', borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  };
  const btnGhost = { ...btn, background: 'transparent', color: 'var(--ocean)', border: '1.5px solid var(--ocean)' };
  const card = {
    background: 'var(--navy-mid)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20,
  };
  const labelSmall = {
    fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ocean)',
    marginBottom: 6, display: 'block',
  };
  const th = {
    textAlign: 'left', padding: '8px 10px',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
    color: 'var(--ocean)', borderBottom: '1px solid rgba(255,255,255,0.08)',
  };
  const td = {
    padding: '9px 10px', borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: 'rgba(255,255,255,0.8)', fontSize: 12, verticalAlign: 'middle',
  };
  const tagGreen = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(74,222,128,0.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.25)' };
  const tagAmber = { ...tagGreen, background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' };
  const tagOcean = { ...tagGreen, background: 'rgba(31,147,224,0.12)', color: 'var(--ocean)', border: '1px solid rgba(31,147,224,0.25)' };

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Trigger */}
      <button style={btn} onClick={() => { setAperto(!aperto); reset(); }}>
        <span className="material-symbols-outlined" style={{ fontSize: 17 }}>upload_file</span>
        Importa da Spiagge.it
      </button>

      {aperto && (
        <div style={{ ...card, marginTop: 12 }}>

          {/* Header */}
          <div style={{ marginBottom: 16 }}>
            <span style={labelSmall}>Import listino CSV · Spiagge.it</span>
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
              Carica uno o più file CSV dal pannello Spiagge.it. Le date vengono lette
              automaticamente dal nome file. I duplicati vengono ignorati.
            </p>
          </div>

          {/* Drop zone */}
          {!anteprima.length && !risultato && (
            <label
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 8, padding: '28px 20px',
                border: '2px dashed rgba(31,147,224,0.3)',
                borderRadius: 'var(--radius-md)', cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(31,147,224,0.6)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(31,147,224,0.3)'}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 32, color: 'var(--ocean)', opacity: 0.65 }}>
                cloud_upload
              </span>
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--text-muted)' }}>
                Trascina i CSV oppure{' '}
                <span style={{ color: 'var(--ocean)', fontWeight: 600 }}>clicca per selezionare</span>
              </span>
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--text-muted)', opacity: 0.5 }}>
                Puoi caricare più file contemporaneamente
              </span>
              <input
                ref={inputRef}
                type="file"
                accept=".csv"
                multiple
                onChange={handleFile}
                style={{ display: 'none' }}
              />
            </label>
          )}

          {/* Errori di parsing */}
          {erroriParsing.length > 0 && (
            <div style={{
              marginTop: 10, padding: '10px 12px',
              background: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.25)',
              borderRadius: 'var(--radius-md)',
            }}>
              {erroriParsing.map((e, i) => (
                <div key={i} style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: '#e05c5c', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 13 }}>warning</span>
                  {e}
                </div>
              ))}
            </div>
          )}

          {/* Anteprima */}
          {anteprima.length > 0 && !risultato && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, marginTop: 4 }}>
                <span style={{ ...labelSmall, margin: 0 }}>Anteprima</span>
                <span style={tagGreen}>{anteprima.length} tariffe trovate</span>
              </div>

              <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-md)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
                  <thead>
                    <tr>
                      <th style={th}>Periodo</th>
                      <th style={th}>Dal → Al</th>
                      {righeOmbrellone.map((r, i) => (
                        <th key={r} style={{ ...th, textAlign: 'right' }}>
                          Ombr. {LETTERS[r]}{i === 0 ? ' ★' : ''}
                        </th>
                      ))}
                      <th style={{ ...th, textAlign: 'right' }}>Gazebo</th>
                      <th style={{ ...th, textAlign: 'right' }}>Macramé</th>
                      <th style={{ ...th, textAlign: 'right' }}>Lettini</th>
                      <th style={{ ...th, textAlign: 'right' }}>Prio.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anteprima.map((t, i) => {
                      const p = t.prezzi;
                      const giorni = Math.round((new Date(t.al) - new Date(t.dal)) / 86400000) + 1;
                      const tagPrio = t.priorita >= 80 ? tagAmber : t.priorita >= 50 ? tagGreen : tagOcean;
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                          <td style={td}>
                            <span style={{ fontWeight: 600, color: '#fff' }}>{t.nome}</span>
                            <span style={{ ...tagOcean, marginLeft: 6, fontSize: 10, padding: '1px 6px' }}>{giorni}gg</span>
                          </td>
                          <td style={{ ...td, fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                            {t.dal} → {t.al}
                          </td>
                          {righeOmbrellone.map((r) => (
                            <td key={r} style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                              €{p[`${ID_OMBRELLONE}_r${r}`] ?? '—'}
                            </td>
                          ))}
                          <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#fff' }}>€{p[ID_GAZEBO]}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <span style={{ fontWeight: 600, color: '#fff' }}>€{p[ID_MACRAUME]}</span>
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 3 }}>+5</span>
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#fff' }}>€{p[ID_AREA_LETTINI]}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <span style={tagPrio}>{t.priorita}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Nota mapping */}
              <div style={{
                marginTop: 10, padding: '9px 12px',
                background: 'rgba(31,147,224,0.06)', border: '1px solid rgba(31,147,224,0.15)',
                borderRadius: 'var(--radius-md)',
                fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4, color: 'var(--ocean)' }}>info</span>
                Prezzi dalla riga <code style={{ background: 'rgba(255,255,255,0.07)', padding: '0 4px', borderRadius: 3 }}>de</code> (giornaliero base).
                Macramé = Fila {LETTERS[righeOmbrellone[0]]} +€5.
                ★ = fronte mare.
                Priorità: periodo più corto vince su quello più lungo in caso di sovrapposizione.
              </div>

              {/* Azioni */}
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button
                  style={{ ...btn, opacity: importando ? 0.6 : 1 }}
                  onClick={handleImport}
                  disabled={importando}
                >
                  {importando
                    ? <><span className="material-symbols-outlined" style={{ fontSize: 15, animation: 'csvSpin 1s linear infinite' }}>progress_activity</span> Importando...</>
                    : <><span className="material-symbols-outlined" style={{ fontSize: 15 }}>save</span> Salva {anteprima.length} tariffe in Firestore</>
                  }
                </button>
                <button style={btnGhost} onClick={reset}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>close</span>
                  Annulla
                </button>
              </div>
            </>
          )}

          {/* Risultato */}
          {risultato && (
            <div style={{ marginTop: 4 }}>
              {risultato.ok.length > 0 && (
                <div style={{
                  padding: '12px 14px',
                  background: 'rgba(74,222,128,0.07)', border: '1px solid rgba(74,222,128,0.2)',
                  borderRadius: 'var(--radius-md)', marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                    <span className="material-symbols-outlined" style={{ color: '#4ade80', fontSize: 18 }}>check_circle</span>
                    <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, color: '#4ade80', fontSize: 13 }}>
                      {risultato.ok.length} tariffe salvate
                    </span>
                  </div>
                  {risultato.ok.map((nome, i) => (
                    <div key={i} style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--text-muted)', paddingLeft: 25 }}>
                      • {nome}
                    </div>
                  ))}
                </div>
              )}
              {risultato.errori.length > 0 && (
                <div style={{
                  padding: '12px 14px',
                  background: 'rgba(224,92,92,0.07)', border: '1px solid rgba(224,92,92,0.2)',
                  borderRadius: 'var(--radius-md)', marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                    <span className="material-symbols-outlined" style={{ color: '#e05c5c', fontSize: 18 }}>error</span>
                    <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, color: '#e05c5c', fontSize: 13 }}>
                      {risultato.errori.length} errori
                    </span>
                  </div>
                  {risultato.errori.map((e, i) => (
                    <div key={i} style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: '#e05c5c', paddingLeft: 25 }}>• {e}</div>
                  ))}
                </div>
              )}
              <button style={{ ...btnGhost, marginTop: 4 }} onClick={() => { reset(); setAperto(false); }}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>check</span>
                Fatto
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes csvSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}